import React, { useState, useEffect } from "react";

const DefaultSuccess = () => {
    const [paymentData, setPaymentData] = useState({
        payment_id: "",
        status: "",
        customer_email: "",
        amount: "",
        currency: ""
    });
    const [particles, setParticles] = useState([]);
    const [checkVisible, setCheckVisible] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        setPaymentData({
            payment_id: urlParams.get("payment_id") || "",
            status: urlParams.get("status") || "success",
            customer_email: urlParams.get("customer_email") || "",
            amount: urlParams.get("amount") || "",
            currency: urlParams.get("currency") || "USD"
        });

        setTimeout(() => setCheckVisible(true), 500);

        // Confetti-style burst particles
        const generated = Array.from({ length: 24 }, (_, i) => ({
            id: i,
            angle: (i / 24) * 360,
            distance: 70 + Math.random() * 70,
            size: 4 + Math.random() * 6,
            color: ["#10b981", "#34d399", "#6ee7b7", "#059669", "#a7f3d0", "#fbbf24"][i % 6],
            duration: 0.7 + Math.random() * 0.4,
            delay: 0.55 + Math.random() * 0.2,
        }));
        setParticles(generated);
    }, []);

    const handleContinue = () => { window.location.href = "/"; };

    const particleStyle = (p) => {
        const rad = (p.angle * Math.PI) / 180;
        return {
            position: "absolute",
            top: "50%", left: "50%",
            width: `${p.size}px`, height: `${p.size}px`,
            borderRadius: i => i % 3 === 0 ? "2px" : "50%",
            background: p.color,
            "--tx": `${Math.cos(rad) * p.distance}px`,
            "--ty": `${Math.sin(rad) * p.distance}px`,
            animation: `particleBurst ${p.duration}s ease-out ${p.delay}s both`,
            pointerEvents: "none",
            marginLeft: `-${p.size / 2}px`,
            marginTop: `-${p.size / 2}px`,
        };
    };

    const css = `
    @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');

    @keyframes slideUp {
      0%   { opacity:0; transform: translateY(40px) scale(0.97); }
      100% { opacity:1; transform: translateY(0) scale(1); }
    }
    @keyframes popIn {
      0%   { opacity:0; transform: scale(0.3) rotate(-20deg); }
      55%  { transform: scale(1.25) rotate(6deg); }
      75%  { transform: scale(0.93) rotate(-3deg); }
      100% { opacity:1; transform: scale(1) rotate(0deg); }
    }
    @keyframes barGrow {
      from { transform: scaleX(0); }
      to   { transform: scaleX(1); }
    }
    @keyframes drawCheck {
      from { stroke-dashoffset: 60; }
      to   { stroke-dashoffset: 0; }
    }
    @keyframes pulse {
      0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.35); }
      50%     { box-shadow: 0 0 0 18px rgba(16,185,129,0); }
    }
    @keyframes ripple {
      0%   { transform: scale(0.8); opacity:0.6; }
      100% { transform: scale(2.4); opacity:0; }
    }
    @keyframes particleBurst {
      0%   { opacity:1; transform: translate(0,0) scale(1); }
      100% { opacity:0; transform: translate(var(--tx), var(--ty)) scale(0); }
    }
    @keyframes fadeSlide {
      from { opacity:0; transform: translateX(-12px); }
      to   { opacity:1; transform: translateX(0); }
    }
    @keyframes scanline {
      0%   { top: -20%; }
      100% { top: 110%; }
    }
    @keyframes floatUp {
      0%,100% { transform: translateY(0); }
      50%     { transform: translateY(-6px); }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position: 200% center; }
    }

    .ds-card {
      animation: slideUp 0.55s cubic-bezier(0.34,1.56,0.64,1) both;
    }
    .ds-icon-wrap {
      animation: popIn 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.4s both, pulse 2.2s ease-in-out 1.2s infinite;
    }
    .ds-bar {
      transform-origin: left;
      animation: barGrow 0.9s cubic-bezier(0.22,1,0.36,1) 0.2s both;
    }
    .ds-amount {
      animation: floatUp 3s ease-in-out 1.5s infinite;
    }
    .ds-btn {
      cursor: pointer;
      transition: transform 0.2s, box-shadow 0.2s;
    }
    .ds-btn:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 14px 36px rgba(16,185,129,0.45) !important;
    }
    .ds-btn:active { transform: scale(0.97); }
    .ds-detail-row {
      animation: fadeSlide 0.4s ease both;
    }
    .ds-ripple {
      animation: ripple 2s ease-out 0.6s infinite;
    }
    .ds-scanline {
      animation: scanline 3.5s linear infinite;
    }
    .ds-check {
      stroke-dasharray: 60;
      stroke-dashoffset: 60;
    }
    .ds-check.visible {
      animation: drawCheck 0.5s cubic-bezier(0.22,1,0.36,1) 0.65s forwards;
    }
    @media (max-width: 480px) {
      .ds-detail-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
      }
    }
    `;

    const hasDetails = paymentData.payment_id || paymentData.amount || paymentData.customer_email;

    return (
        <>
            <style>{css}</style>
            <div style={{
                minHeight: "100vh",
                background: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf8 40%, #f5fff9 70%, #d1fae5 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "20px", fontFamily: "'Roboto', sans-serif",
                position: "relative", overflow: "hidden",
            }}>
                {/* Decorative BG circles */}
                <div style={{ position: "absolute", top: "-80px", right: "-80px", width: "340px", height: "340px", borderRadius: "50%", background: "rgba(16,185,129,0.07)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", bottom: "-60px", left: "-60px", width: "260px", height: "260px", borderRadius: "50%", background: "rgba(16,185,129,0.05)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", top: "35%", left: "7%", width: "80px", height: "80px", borderRadius: "50%", background: "rgba(16,185,129,0.04)", pointerEvents: "none" }} />

                <div className="ds-card" style={{
                    maxWidth: "480px", width: "100%",
                    background: "#ffffff", borderRadius: "20px",
                    boxShadow: "0 20px 60px rgba(16,185,129,0.12), 0 4px 20px rgba(0,0,0,0.06)",
                    overflow: "hidden", border: "1px solid rgba(16,185,129,0.12)",
                    position: "relative",
                }}>
                    {/* Scanline shimmer */}
                    <div style={{ position: "absolute", inset: 0, overflow: "hidden", borderRadius: "20px", pointerEvents: "none", zIndex: 0 }}>
                        <div className="ds-scanline" style={{
                            position: "absolute", left: 0, right: 0, height: "60px",
                            background: "linear-gradient(180deg,transparent,rgba(16,185,129,0.04),transparent)",
                        }} />
                    </div>

                    {/* Top accent bar */}
                    <div className="ds-bar" style={{
                        height: "5px",
                        background: "linear-gradient(90deg, #10b981 0%, #34d399 50%, #059669 100%)",
                        position: "relative", zIndex: 1,
                    }} />

                    <div style={{ padding: "40px 36px 36px", textAlign: "center", position: "relative", zIndex: 1 }}>

                        {/* Icon with ripple + burst */}
                        <div style={{ position: "relative", display: "inline-block", marginBottom: "24px" }}>
                            {/* Ripple rings */}
                            {[0, 1].map(i => (
                                <div key={i} className="ds-ripple" style={{
                                    position: "absolute", inset: "-10px", borderRadius: "50%",
                                    border: "2px solid rgba(16,185,129,0.3)",
                                    animationDelay: `${0.6 + i * 0.7}s`,
                                }} />
                            ))}
                            {/* Particles */}
                            {particles.map(p => (
                                <div key={p.id} style={{
                                    position: "absolute", top: "50%", left: "50%",
                                    width: `${p.size}px`, height: `${p.size}px`,
                                    borderRadius: p.id % 3 === 0 ? "2px" : "50%",
                                    background: p.color,
                                    "--tx": `${Math.cos((p.angle * Math.PI) / 180) * p.distance}px`,
                                    "--ty": `${Math.sin((p.angle * Math.PI) / 180) * p.distance}px`,
                                    animation: `particleBurst ${p.duration}s ease-out ${p.delay}s both`,
                                    pointerEvents: "none",
                                    marginLeft: `-${p.size / 2}px`,
                                    marginTop: `-${p.size / 2}px`,
                                }} />
                            ))}
                            {/* Icon circle */}
                            <div className="ds-icon-wrap" style={{
                                width: "80px", height: "80px", borderRadius: "50%",
                                background: "linear-gradient(135deg, #d1fae5, #a7f3d0)",
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                border: "2px solid rgba(16,185,129,0.25)",
                                position: "relative",
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"
                                    viewBox="0 0 24 24" fill="none" stroke="#10b981"
                                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                    <polyline
                                        className={`ds-check${checkVisible ? " visible" : ""}`}
                                        points="22 4 12 14.01 9 11.01"
                                    />
                                </svg>
                            </div>
                        </div>

                        {/* Title */}
                        <h1 style={{
                            fontSize: "30px", fontWeight: "700", color: "#111827",
                            marginBottom: "10px", letterSpacing: "-0.6px", lineHeight: "1.2",
                        }}>
                            Payment Successful!
                        </h1>
                        <p style={{
                            fontSize: "15px", color: "#6b7280", marginBottom: "28px", lineHeight: "1.65",
                        }}>
                            Thank you! Your transaction has been<br />completed successfully.
                        </p>

                        {/* Amount highlight badge */}
                        {paymentData.amount && (
                            <div className="ds-amount" style={{
                                display: "inline-flex", alignItems: "baseline", gap: "6px",
                                background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
                                border: "1px solid rgba(16,185,129,0.2)",
                                borderRadius: "50px", padding: "10px 24px",
                                marginBottom: "24px",
                            }}>
                                <span style={{ fontSize: "13px", fontWeight: "500", color: "#059669" }}>Amount Paid</span>
                                <span style={{ fontSize: "22px", fontWeight: "700", color: "#047857", letterSpacing: "-0.5px" }}>
                                    {paymentData.amount} {paymentData.currency}
                                </span>
                            </div>
                        )}

                        {/* Details card */}
                        {hasDetails && (
                            <div style={{
                                background: "linear-gradient(135deg, #f0fdf4, #ecfdf5)",
                                borderRadius: "14px", marginBottom: "28px",
                                textAlign: "left", border: "1px solid rgba(16,185,129,0.12)",
                                overflow: "hidden",
                            }}>
                                {paymentData.payment_id && (
                                    <div className="ds-detail-row" style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "13px 20px", borderBottom: "1px solid rgba(16,185,129,0.08)",
                                        animationDelay: "0.8s",
                                    }}>
                                        <span style={{ fontSize: "13px", fontWeight: "500", color: "#9ca3af", letterSpacing: "0.3px" }}>Payment Intent ID</span>
                                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151", fontFamily: "monospace", background: "#fff", padding: "2px 8px", borderRadius: "6px", border: "1px solid #e5e7eb", wordBreak: "break-all" }}>{paymentData.payment_id}</span>
                                    </div>
                                )}
                                {paymentData.customer_email && (
                                    <div className="ds-detail-row" style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "13px 20px",
                                        animationDelay: "0.95s",
                                    }}>
                                        <span style={{ fontSize: "13px", fontWeight: "500", color: "#9ca3af" }}>Email</span>
                                        <span style={{ fontSize: "13px", fontWeight: "600", color: "#374151" }}>{paymentData.customer_email}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Status badge */}
                        <div style={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            background: "#ecfdf5", border: "1px solid rgba(16,185,129,0.25)",
                            borderRadius: "50px", padding: "6px 16px", marginBottom: "28px",
                        }}>
                            <div style={{
                                width: "7px", height: "7px", borderRadius: "50%", background: "#10b981",
                                boxShadow: "0 0 0 3px rgba(16,185,129,0.25)",
                            }} />
                            <span style={{ fontSize: "12px", fontWeight: "600", color: "#059669", letterSpacing: "0.4px" }}>TRANSACTION CONFIRMED</span>
                        </div>

                        {/* Footer */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                            paddingTop: "16px", borderTop: "1px solid #f3f4f6",
                        }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0110 0v4" />
                            </svg>
                            <span style={{ fontSize: "12px", color: "#9ca3af" }}>Secure payment processed by PayBito Pro</span>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DefaultSuccess;